/**
 * The words the audit surfaces say (#2444 phase 4) — pure functions, no React.
 *
 * People read words, never machine text (marble-uiux non-negotiable 6): an
 * action key (`record.updated`), a source kind (`api_integration`) and a
 * posture code (`chain.integrity`) are identifiers, and each one is turned into
 * a sentence here, once, for every surface that shows it. A value this file
 * does not know is said as "an unrecognised …", never printed raw — except a
 * posture code, which is a governed identifier an operator must be able to
 * quote, and is shown in a `code` element beside its "unrecognised" title.
 */
import { AUDIT_RANGE_MAX_DAYS, type PostureStatus } from '@solum/sdk'

/* ── time ─────────────────────────────────────────────────────────────────── */

export interface Zone { locale?: string; timeZone?: string }

/** "24 Sep 2026, 10:04" in the reader's locale and zone; `—` when absent. */
export function formatWhen(iso: string | null | undefined, zone: Zone = {}): string {
  if (!iso) return '—'
  const t = new Date(iso)
  if (Number.isNaN(t.getTime())) return '—'
  try {
    return new Intl.DateTimeFormat(zone.locale ?? 'en-GB', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
      ...(zone.timeZone ? { timeZone: zone.timeZone } : {}),
    }).format(t)
  } catch {
    return t.toISOString().slice(0, 16).replace('T', ' ')
  }
}

/** A duration a person reads: "340 ms", "2.4 s". */
export function formatDuration(ms: number | null): string {
  if (ms == null) return 'Duration not recorded'
  if (ms < 1000) return `${ms} ms`
  return `${(ms / 1000).toFixed(ms < 10_000 ? 1 : 0)} s`
}

/* ── the date range the filter bar holds (calendar days) ──────────────────── */

const DAY = 86_400_000
const pad = (n: number) => String(n).padStart(2, '0')
/** A local calendar date as `YYYY-MM-DD`, the `DatePicker` value shape. */
export function isoDay(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** The default: the last seven calendar days, today included. */
export function defaultDays(now: Date = new Date()): { fromDay: string; toDay: string } {
  return { fromDay: isoDay(new Date(now.getTime() - 6 * DAY)), toDay: isoDay(now) }
}

/**
 * Calendar days → the projection's instants. The END is the midnight AFTER the
 * last chosen day, so "25 Aug – 24 Sep" is exactly 31 days and is allowed,
 * and a person choosing "today" sees today's events.
 */
export function daysToRange(fromDay: string, toDay: string): { from: string; to: string } {
  const start = fromDay ? new Date(`${fromDay}T00:00:00`) : null
  const end = toDay ? new Date(new Date(`${toDay}T00:00:00`).getTime() + DAY) : null
  return {
    from: start && !Number.isNaN(start.getTime()) ? start.toISOString() : '',
    to: end && !Number.isNaN(end.getTime()) ? end.toISOString() : '',
  }
}

export const RANGE_HINT = `Up to ${AUDIT_RANGE_MAX_DAYS} days per search.`

/* ── actions ──────────────────────────────────────────────────────────────── */

const VERBS: Readonly<Record<string, string>> = {
  created: 'created', updated: 'updated', deleted: 'deleted', viewed: 'viewed', read: 'read',
  restored: 'restored', archived: 'archived', exported: 'exported', redacted: 'redacted',
}

/** `record.updated` → "updated"; `tenders.bid_submitted` → "bid submitted". */
export function actionWords(action: string): string {
  const last = action.split('.').pop() ?? ''
  if (VERBS[last]) return VERBS[last]!
  const words = last.replace(/[_-]+/g, ' ').trim()
  return words && /^[a-z ]+$/i.test(words) ? words.toLowerCase() : 'an unrecognised action'
}

/** The operation filter's choices — what the timeline can be narrowed to. */
export const OPERATION_OPTIONS = [
  { value: 'create', label: 'Created' },
  { value: 'update', label: 'Updated' },
  { value: 'delete', label: 'Deleted' },
  { value: 'read', label: 'Viewed' },
  { value: 'act', label: 'Other action' },
] as const

/**
 * Who acted, in words. A null actor is a system or installer act (p4-api §1:
 * "null = system/installer") and is said "System" — the projection has
 * already resolved every person it can, so null is not "unknown".
 */
export function actorWords(actorLabel: string | null, onBehalfOfLabel: string | null = null): string {
  const who = actorLabel ?? 'System'
  return onBehalfOfLabel ? `${who} on behalf of ${onBehalfOfLabel}` : who
}

/** One timeline event as a sentence: "Ada Lovelace updated Organization “Q8”". */
export function eventSentence(row: {
  action: string; actorLabel: string | null; onBehalfOfLabel?: string | null
  entityLabel: string | null; recordLabel: string | null
}): string {
  const who = actorWords(row.actorLabel, row.onBehalfOfLabel ?? null)
  const what = row.entityLabel ?? 'a record'
  const which = row.recordLabel ? ` “${row.recordLabel}”` : ''
  /* A plain record verb reads as a sentence; a module's own act ("invoice
     issued") is not a verb that takes the record as its object, so it is
     said beside it rather than forced into one. */
  const last = row.action.split('.').pop() ?? ''
  return VERBS[last]
    ? `${who} ${VERBS[last]} ${what}${which}`
    : `${who} · ${actionWords(row.action)} · ${what}${which}`
}

/* ── tool outcomes ────────────────────────────────────────────────────────── */

export const OUTCOME_PRESENTATION: Readonly<Record<string, { label: string; tone: 'good' | 'warn' | 'critical' }>> = {
  ok: { label: 'Succeeded', tone: 'good' },
  refused: { label: 'Refused', tone: 'warn' },
  error: { label: 'Failed', tone: 'critical' },
}

export function outcomePresentation(outcome: string) {
  return OUTCOME_PRESENTATION[outcome] ?? { label: 'Unrecognised outcome', tone: 'warn' as const }
}

export const SURFACE_LABELS: Readonly<Record<string, string>> = { mcp: 'MCP connector', copilot: 'Copilot' }
export const surfaceLabel = (surface: string | null) => (surface && SURFACE_LABELS[surface]) || 'Unknown surface'

/* ── the security posture ─────────────────────────────────────────────────── */

/**
 * How each posture STATUS is drawn. The status is the server's verdict
 * (`ok` / `warn` / `fail` / `info` / `not_installed`, p4-api §6); the console
 * never re-derives it from the count.
 */
export const POSTURE_STATUS: Readonly<Record<PostureStatus, { label: string; tone: 'good' | 'warn' | 'critical' | 'neutral' }>> = {
  ok: { label: 'Healthy', tone: 'good' },
  warn: { label: 'Needs attention', tone: 'warn' },
  fail: { label: 'Failing', tone: 'critical' },
  info: { label: 'For information', tone: 'neutral' },
  not_installed: { label: 'Not installed', tone: 'neutral' },
}

/** The posture's AREAS, in the order an operator reads them, with their headings. */
export const POSTURE_AREAS: readonly { key: string; title: string; sub: string }[] = [
  { key: 'chain', title: 'Audit chain', sub: 'Whether the tamper-evident chain still verifies.' },
  { key: 'capture', title: 'Change capture', sub: 'Which entities record every change.' },
  { key: 'request_gate', title: 'Request gate', sub: 'The receipt gate every API request passes.' },
  { key: 'receipts', title: 'Request receipts', sub: 'The newest receipt, anchors and their verification.' },
  { key: 'recorder', title: 'Recorder health', sub: 'Requests and tool calls that could not be recorded.' },
  { key: 'source_clients', title: 'Source clients', sub: 'OAuth bindings and connector principals.' },
  { key: 'logging', title: 'Database logging', sub: 'Server log settings (values only).' },
  { key: 'pgaudit', title: 'pgAudit', sub: 'The database audit extension.' },
]

export interface PostureCopy { title: string; explain: (count: number | null) => string }

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`
const n0 = (count: number | null) => count ?? 0

/**
 * The posture checks in words, by EXACT code. Codes with a variable tail
 * (`request_capture_failure.<code>`, `oauth_binding.enabled.<surface>`,
 * `request_receipt.verify.<key>`) are matched by prefix below. A code this
 * console does not know renders as "Unrecognised check" with the code shown
 * in a `code` element, so an operator can quote it.
 */
const EXACT: Readonly<Record<string, PostureCopy>> = {
  'audit_chain.intact': { title: 'Chain integrity', explain: (n) => `The chain holds ${plural(n0(n), 'row', 'rows')}.` },
  'audit_chain.v3_rows': { title: 'Rows with source and trust', explain: (n) => `${plural(n0(n), 'row carries', 'rows carry')} the source, trust and correlation fields.` },
  'change_capture.armed': { title: 'Armed entities', explain: (n) => `${plural(n0(n), 'entity records', 'entities record')} every change.` },
  'change_capture.exempt': { title: 'Exempt entities', explain: (n) => `${plural(n0(n), 'entity is', 'entities are')} exempt from change capture by declaration.` },
  'change_capture.unarmed': { title: 'Unarmed entities', explain: (n) => n0(n) ? `${plural(n0(n), 'entity records', 'entities record')} no changes and ${n0(n) === 1 ? 'is' : 'are'} not declared exempt.` : 'Every entity is armed or declared exempt.' },
  'change_capture.flag_mismatch': { title: 'Capture flag mismatches', explain: (n) => n0(n) ? `${plural(n0(n), 'entity’s', 'entities’')} capture flag disagrees with ${n0(n) === 1 ? 'its' : 'their'} trigger.` : 'Every capture flag agrees with its trigger.' },
  'request_gate.signing_key': { title: 'Receipt signing key', explain: () => 'The key the gate signs receipt contexts with.' },
  'request_gate.writer_granted': { title: 'Receipt writer', explain: () => 'The gate may write request receipts.' },
  'request_gate.failure_noter_granted': { title: 'Failure recorder', explain: () => 'The gate may record a request it could not receipt.' },
  'request_receipt.last': { title: 'Newest receipt', explain: (n) => `${plural(n0(n), 'receipt is', 'receipts are')} held.` },
  'request_receipt.last_anchor': { title: 'Newest anchor', explain: (n) => `${plural(n0(n), 'anchor is', 'anchors are')} held.` },
  'request_receipt.verification': { title: 'Receipt verification', explain: () => 'Verification of the held receipts.' },
  'request_receipt.scheduled_anchoring': { title: 'Scheduled anchoring', explain: () => 'Receipts are anchored on a schedule.' },
  'request_receipt.verify.intact': { title: 'Receipts verify', explain: () => 'Every held receipt matches its anchors.' },
  'request_receipt.verify.days': { title: 'Days verified', explain: (n) => `${plural(n0(n), 'day', 'days')} of receipts were checked.` },
  'request_receipt.verify.days_intact': { title: 'Days intact', explain: (n) => `${plural(n0(n), 'day verifies', 'days verify')} end to end.` },
  'request_receipt.verify.altered': { title: 'Altered receipts', explain: (n) => n0(n) ? `${plural(n0(n), 'receipt was', 'receipts were')} altered after capture.` : 'No receipt was altered.' },
  'request_receipt.verify.removed': { title: 'Removed receipts', explain: (n) => n0(n) ? `${plural(n0(n), 'receipt is', 'receipts are')} missing outside disposal.` : 'No receipt is missing.' },
  'request_receipt.verify.inserted': { title: 'Inserted receipts', explain: (n) => n0(n) ? `${plural(n0(n), 'receipt was', 'receipts were')} inserted after anchoring.` : 'No receipt was inserted after anchoring.' },
  'request_receipt.verify.tampered_before_disposal': { title: 'Tampered before disposal', explain: (n) => n0(n) ? `${plural(n0(n), 'disposed day was', 'disposed days were')} altered before disposal.` : 'Nothing was altered before disposal.' },
  'request_receipt.verify.ledger_mismatch': { title: 'Disposal ledger mismatches', explain: (n) => n0(n) ? `${plural(n0(n), 'disposal disagrees', 'disposals disagree')} with the ledger.` : 'Every disposal agrees with the ledger.' },
  'request_receipt.verify.unanchored_days': { title: 'Unanchored days', explain: (n) => n0(n) ? `${plural(n0(n), 'day has', 'days have')} no anchor.` : 'Every day is anchored.' },
  'request_receipt.verify.disposed_unanchored': { title: 'Disposed without an anchor', explain: (n) => n0(n) ? `${plural(n0(n), 'day was', 'days were')} disposed before being anchored.` : 'No day was disposed before being anchored.' },
  'tool_invocation.uncorrelated_24h': { title: 'Uncorrelated tool calls', explain: (n) => n0(n) ? `${plural(n0(n), 'tool call', 'tool calls')} in the last 24 hours carried no correlation.` : 'Every tool call in the last 24 hours carried a correlation.' },
  'oauth_binding.disabled': { title: 'Disabled bindings', explain: (n) => `${plural(n0(n), 'OAuth binding is', 'OAuth bindings are')} disabled.` },
  'oauth_binding.orphaned': { title: 'Orphaned bindings', explain: (n) => n0(n) ? `${plural(n0(n), 'binding points', 'bindings point')} at a client that no longer exists.` : 'Every binding points at a live client.' },
  'connector_principal.bound': { title: 'Connector principals', explain: (n) => `${plural(n0(n), 'connector acts', 'connectors act')} as a registered service principal.` },
  'connector_principal.disabled_referenced': { title: 'Disabled connectors still referenced', explain: (n) => n0(n) ? `${plural(n0(n), 'disabled connector is', 'disabled connectors are')} still referenced.` : 'No disabled connector is referenced.' },
  log_min_messages: { title: 'Minimum log level', explain: () => 'The lowest severity the database writes to its log.' },
  log_statement: { title: 'Statement logging', explain: () => 'Which statements the database writes to its log.' },
  log_min_duration_statement: { title: 'Slow statement logging', explain: () => 'The duration above which a statement is logged.' },
  'pgaudit.loaded': { title: 'Loaded', explain: () => 'Whether pgAudit is loaded into the database.' },
  'pgaudit.log': { title: 'Audited classes', explain: () => 'Which statement classes pgAudit records.' },
  'pgaudit.log_parameter': { title: 'Parameter logging', explain: () => 'Whether pgAudit writes statement parameters.' },
  'pgaudit.log_relation': { title: 'Relation logging', explain: () => 'Whether pgAudit names each relation touched.' },
  'pgaudit.api_role_widened': { title: 'API role auditing', explain: (n) => n0(n) ? `${plural(n0(n), 'API role is', 'API roles are')} audited more widely than the default.` : 'No API role is audited more widely than the default.' },
}

const PREFIXED: readonly { prefix: string; copy: (tail: string) => PostureCopy }[] = [
  { prefix: 'request_capture_failure.', copy: (tail) => ({ title: `Unrecorded requests: ${words(tail)}`, explain: (n) => n0(n) ? `${plural(n0(n), 'request', 'requests')} could not be recorded (${words(tail)}).` : 'None in the retention window.' }) },
  { prefix: 'oauth_binding.enabled.', copy: (tail) => ({ title: `Enabled bindings: ${SURFACE_WORDS[tail] ?? words(tail)}`, explain: (n) => `${plural(n0(n), 'OAuth client is', 'OAuth clients are')} bound to this surface.` }) },
  { prefix: 'request_receipt.verify.', copy: (tail) => ({ title: `Verification: ${words(tail)}`, explain: (n) => n == null ? 'No count reported.' : `${plural(n, 'receipt', 'receipts')}.` }) },
]

const SURFACE_WORDS: Readonly<Record<string, string>> = { mcp: 'MCP', console: 'Console', portal: 'Portal', copilot: 'Copilot' }

/** `delivery_failed` → "delivery failed"; anything not plain words → "unnamed". */
function words(tail: string): string {
  const w = tail.replace(/[_.-]+/g, ' ').trim()
  return /^[a-z0-9 ]+$/i.test(w) && w ? w : 'unnamed'
}

/**
 * A posture DETAIL is a server setting value (`ddl,role`, `warning`, `-1`) and
 * is shown as the identifier it is. The one detail that is a verdict rather
 * than a setting is said in words.
 */
const DETAIL_WORDS: Readonly<Record<string, string>> = {
  platform_admin_only: 'Shown to platform administrators only',
}
export function postureDetail(detail: string): { words: string } | { setting: string } {
  return DETAIL_WORDS[detail] ? { words: DETAIL_WORDS[detail]! } : { setting: detail }
}

/** The copy for a code, or the honest "unrecognised" fallback. */
export function postureCopy(code: string): PostureCopy & { known: boolean } {
  const exact = EXACT[code]
  if (exact) return { ...exact, known: true }
  const match = PREFIXED.find((p) => code.startsWith(p.prefix))
  if (match) return { ...match.copy(code.slice(match.prefix.length)), known: true }
  return {
    title: 'Unrecognised check',
    explain: () => 'This deployment reports a check this Console does not describe yet.',
    known: false,
  }
}
